/**
 * PropertiesPanel - Edit properties of selected element
 */

import React, { useState, useRef } from 'react';
import { EmailBuilderState, EmailBuilderActions } from './useEmailBuilder';
import { 
  ContentBlock, 
  TextBlock, 
  ImageBlock, 
  ButtonBlock, 
  DividerBlock, 
  SpacerBlock,
  FooterBlock,
  SocialLinks,
  HeadingLevel,
  FontFamily,
  FontWeight,
  HEADING_FONT_SIZES,
  FONT_FAMILIES,
  FONT_WEIGHTS,
} from './types';
import { 
  Settings, 
  Type, 
  Image, 
  MousePointer, 
  Minus, 
  ArrowUpDown,
  AlignLeft,
  AlignCenter,
  AlignRight,
  ChevronDown,
  Plus,
  LayoutTemplate,
  Upload,
  Loader2,
  Trash2,
} from 'lucide-react';
import { uploadEmailImage } from '../../../utils/notificationsBroadcastApi';
import { BackgroundSize, BackgroundPosition, BackgroundRepeat } from './types';

interface PropertiesPanelProps {
  state: EmailBuilderState;
  actions: EmailBuilderActions;
}

export const PropertiesPanel: React.FC<PropertiesPanelProps> = ({ state, actions }) => {
  const { selection, document } = state;

  // Find the selected element
  const getSelectedElement = () => {
    if (selection.type === 'row' && selection.rowId) {
      return {
        type: 'row' as const,
        data: document.rows.find(r => r.id === selection.rowId),
      };
    }

    if (selection.type === 'block' && selection.blockId) {
      for (const row of document.rows) {
        for (const col of row.columns) {
          const block = col.blocks.find(b => b.id === selection.blockId);
          if (block) {
            return { type: 'block' as const, data: block };
          }
        }
      }
    }

    return null;
  };

  const selected = getSelectedElement();

  if (!selected) {
    return (
      <div className="p-4">
        {/* Global Settings */}
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">
          Email Settings
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Background Color
            </label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={document.globalStyles.backgroundColor}
                onChange={(e) => actions.updateGlobalStyles({ backgroundColor: e.target.value })}
                className="w-10 h-10 rounded cursor-pointer border border-gray-300"
              />
              <input
                type="text"
                value={document.globalStyles.backgroundColor}
                onChange={(e) => actions.updateGlobalStyles({ backgroundColor: e.target.value })}
                className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Content Width
            </label>
            <input
              type="number"
              value={document.globalStyles.contentWidth}
              onChange={(e) => actions.updateGlobalStyles({ contentWidth: parseInt(e.target.value) })}
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
              min={400}
              max={800}
            />
          </div>
        </div>

        <div className="mt-6 p-4 bg-gray-50  rounded-lg">
          <p className="text-sm text-gray-500  text-center">
            <Settings className="w-5 h-5 mx-auto mb-2 opacity-50" />
            Select a row or block to edit its properties
          </p>
        </div>
      </div>
    );
  }

  if (selected.type === 'row' && selected.data) {
    return <RowProperties row={selected.data} onUpdate={actions.updateRow} />;
  }

  if (selected.type === 'block' && selected.data) {
    return <BlockProperties block={selected.data} onUpdate={actions.updateBlock} />;
  }

  return null;
};

// =============================================================================
// Row Properties
// =============================================================================

interface RowPropertiesProps {
  row: NonNullable<ReturnType<typeof Array.prototype.find>>;
  onUpdate: (rowId: string, updates: any) => void;
}

const RowProperties: React.FC<RowPropertiesProps> = ({ row, onUpdate }) => {
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const publicUrl = await uploadEmailImage(file);
      onUpdate(row.id, { 
        backgroundImage: publicUrl,
        backgroundSize: row.backgroundSize || 'cover',
        backgroundPosition: row.backgroundPosition || 'center',
        backgroundRepeat: row.backgroundRepeat || 'no-repeat',
      });
    } catch (error) {
      console.error('Failed to upload image:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveBackground = () => {
    onUpdate(row.id, { 
      backgroundImage: undefined,
      backgroundSize: undefined,
      backgroundPosition: undefined,
      backgroundRepeat: undefined,
    });
  };

  const backgroundSizeOptions: { value: BackgroundSize; label: string }[] = [
    { value: 'cover', label: 'Cover (fill)' },
    { value: 'contain', label: 'Contain (fit)' },
    { value: '100% 100%', label: 'Stretch' },
    { value: 'auto', label: 'Original size' },
  ];

  const backgroundPositionOptions: { value: BackgroundPosition; label: string }[] = [
    { value: 'center', label: 'Center' },
    { value: 'top', label: 'Top' },
    { value: 'bottom', label: 'Bottom' },
    { value: 'left', label: 'Left' },
    { value: 'right', label: 'Right' },
    { value: 'top left', label: 'Top Left' },
    { value: 'top right', label: 'Top Right' },
    { value: 'bottom left', label: 'Bottom Left' },
    { value: 'bottom right', label: 'Bottom Right' },
  ];

  const backgroundRepeatOptions: { value: BackgroundRepeat; label: string }[] = [
    { value: 'no-repeat', label: 'No Repeat' },
    { value: 'repeat', label: 'Repeat' },
    { value: 'repeat-x', label: 'Repeat Horizontal' },
    { value: 'repeat-y', label: 'Repeat Vertical' },
  ];

  return (
    <div className="p-4">
      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">
        Row Properties
      </h3>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Background Color
          </label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={row.backgroundColor}
              onChange={(e) => onUpdate(row.id, { backgroundColor: e.target.value })}
              className="w-10 h-10 rounded cursor-pointer border border-gray-300"
            />
            <input
              type="text"
              value={row.backgroundColor}
              onChange={(e) => onUpdate(row.id, { backgroundColor: e.target.value })}
              className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
            />
          </div>
        </div>

        {/* Background Image Section */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Background Image
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={handleFileSelect}
            className="hidden"
          />
          
          {row.backgroundImage ? (
            <div className="space-y-3">
              {/* Preview */}
              <div className="relative rounded-lg overflow-hidden border border-gray-200">
                <img 
                  src={row.backgroundImage} 
                  alt="Background" 
                  className="w-full h-20 object-cover"
                />
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center gap-2 opacity-0 hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="px-2 py-1 text-xs bg-white text-gray-700 rounded hover:bg-gray-100"
                  >
                    Replace
                  </button>
                  <button
                    onClick={handleRemoveBackground}
                    className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>

              {/* Size Option */}
              <div>
                <label className="block text-xs text-gray-500 mb-1">Size</label>
                <select
                  value={row.backgroundSize || 'cover'}
                  onChange={(e) => onUpdate(row.id, { backgroundSize: e.target.value as BackgroundSize })}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                >
                  {backgroundSizeOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Position Option */}
              <div>
                <label className="block text-xs text-gray-500 mb-1">Position</label>
                <select
                  value={row.backgroundPosition || 'center'}
                  onChange={(e) => onUpdate(row.id, { backgroundPosition: e.target.value as BackgroundPosition })}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                >
                  {backgroundPositionOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Repeat Option */}
              <div>
                <label className="block text-xs text-gray-500 mb-1">Repeat</label>
                <select
                  value={row.backgroundRepeat || 'no-repeat'}
                  onChange={(e) => onUpdate(row.id, { backgroundRepeat: e.target.value as BackgroundRepeat })}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                >
                  {backgroundRepeatOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          ) : (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50/50 transition-colors disabled:opacity-50"
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Upload Background Image
                </>
              )}
            </button>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Padding
          </label>
          <input
            type="range"
            value={row.padding}
            onChange={(e) => onUpdate(row.id, { padding: parseInt(e.target.value) })}
            className="w-full"
            min={0}
            max={60}
          />
          <div className="text-xs text-gray-500 text-center">{row.padding}px</div>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// Block Properties
// =============================================================================

interface BlockPropertiesProps {
  block: ContentBlock;
  onUpdate: (blockId: string, updates: Partial<ContentBlock>) => void;
}

const BlockProperties: React.FC<BlockPropertiesProps> = ({ block, onUpdate }) => {
  const renderProperties = () => {
    switch (block.type) {
      case 'text':
        return <TextBlockProperties block={block} onUpdate={onUpdate} />;
      case 'image':
        return <ImageBlockProperties block={block} onUpdate={onUpdate} />;
      case 'button':
        return <ButtonBlockProperties block={block} onUpdate={onUpdate} />;
      case 'divider':
        return <DividerBlockProperties block={block} onUpdate={onUpdate} />;
      case 'spacer':
        return <SpacerBlockProperties block={block} onUpdate={onUpdate} />;
      case 'footer':
        return <FooterBlockProperties block={block} onUpdate={onUpdate} />;
      default:
        return null;
    }
  };

  const getIcon = () => {
    switch (block.type) {
      case 'text': return <Type className="w-4 h-4" />;
      case 'image': return <Image className="w-4 h-4" />;
      case 'button': return <MousePointer className="w-4 h-4" />;
      case 'divider': return <Minus className="w-4 h-4" />;
      case 'spacer': return <ArrowUpDown className="w-4 h-4" />;
      case 'footer': return <LayoutTemplate className="w-4 h-4" />;
    }
  };

  return (
    <div className="p-4">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center text-blue-600">
          {getIcon()}
        </div>
        <h3 className="text-sm font-semibold text-gray-900 capitalize">
          {block.type} Block
        </h3>
      </div>
      
      {renderProperties()}
    </div>
  );
};

// =============================================================================
// Individual Block Property Editors
// =============================================================================

const TextBlockProperties: React.FC<{
  block: TextBlock;
  onUpdate: (blockId: string, updates: Partial<TextBlock>) => void;
}> = ({ block, onUpdate }) => {
  // Handle heading level change with auto font-size update
  const handleHeadingChange = (level: HeadingLevel) => {
    onUpdate(block.id, { 
      headingLevel: level, 
      fontSize: HEADING_FONT_SIZES[level] 
    });
  };

  // Handle font size increment/decrement
  const adjustFontSize = (delta: number) => {
    const newSize = Math.min(72, Math.max(10, (block.fontSize || 16) + delta));
    onUpdate(block.id, { fontSize: newSize });
  };

  // Handle letter spacing increment/decrement
  const adjustLetterSpacing = (delta: number) => {
    const newSpacing = Math.min(10, Math.max(0, (block.letterSpacing || 0) + delta));
    onUpdate(block.id, { letterSpacing: newSpacing });
  };

  const headingOptions: { value: HeadingLevel; label: string }[] = [
    { value: 'h1', label: 'H1' },
    { value: 'h2', label: 'H2' },
    { value: 'h3', label: 'H3' },
    { value: 'h4', label: 'H4' },
    { value: 'p', label: 'Paragraph' },
  ];

  const lineHeightOptions = [
    { value: 1.2, label: 'Tight' },
    { value: 1.4, label: 'Normal' },
    { value: 1.6, label: 'Relaxed' },
    { value: 2.0, label: 'Loose' },
  ];

  return (
    <div className="space-y-4">
      {/* Title / Heading Level */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Title
        </label>
        <div className="relative">
          <select
            value={block.headingLevel || 'p'}
            onChange={(e) => handleHeadingChange(e.target.value as HeadingLevel)}
            className="w-full appearance-none px-3 py-2 text-sm border border-gray-300  rounded-lg bg-white  text-gray-900  cursor-pointer pr-8"
          >
            {headingOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {/* Font Family */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Font Family
        </label>
        <div className="relative">
          <select
            value={block.fontFamily || 'Arial'}
            onChange={(e) => onUpdate(block.id, { fontFamily: e.target.value as FontFamily })}
            className="w-full appearance-none px-3 py-2 text-sm border border-gray-300  rounded-lg bg-white  text-gray-900  cursor-pointer pr-8"
          >
            {FONT_FAMILIES.map((font) => (
              <option key={font.value} value={font.value} style={{ fontFamily: font.value }}>
                {font.label}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {/* Font Weight */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Font Weight
        </label>
        <div className="relative">
          <select
            value={block.fontWeight || 'normal'}
            onChange={(e) => onUpdate(block.id, { fontWeight: e.target.value as FontWeight })}
            className="w-full appearance-none px-3 py-2 text-sm border border-gray-300  rounded-lg bg-white  text-gray-900  cursor-pointer pr-8"
          >
            {FONT_WEIGHTS.map((weight) => (
              <option key={weight.value} value={weight.value}>
                {weight.label}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {/* Font Size with +/- controls */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Font Size
        </label>
        <div className="flex items-center gap-1">
          <button
            onClick={() => adjustFontSize(-2)}
            className="w-8 h-8 flex items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-100"
          >
            <Minus className="w-3 h-3" />
          </button>
          <input
            type="number"
            value={block.fontSize || 16}
            onChange={(e) => onUpdate(block.id, { fontSize: parseInt(e.target.value) || 16 })}
            className="flex-1 text-center px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-white text-gray-900"
            min={10}
            max={72}
          />
          <button
            onClick={() => adjustFontSize(2)}
            className="w-8 h-8 flex items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-100"
          >
            <Plus className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Text Color */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Text Color
        </label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={block.color || '#1e293b'}
            onChange={(e) => onUpdate(block.id, { color: e.target.value })}
            className="w-10 h-10 rounded cursor-pointer border border-gray-300"
          />
          <input
            type="text"
            value={block.color || '#1e293b'}
            onChange={(e) => onUpdate(block.id, { color: e.target.value })}
            className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-white text-gray-900"
          />
        </div>
      </div>

      {/* Link Color */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Link Color
        </label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={block.linkColor || '#1e40af'}
            onChange={(e) => onUpdate(block.id, { linkColor: e.target.value })}
            className="w-10 h-10 rounded cursor-pointer border border-gray-300"
          />
          <input
            type="text"
            value={block.linkColor || '#1e40af'}
            onChange={(e) => onUpdate(block.id, { linkColor: e.target.value })}
            className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-white text-gray-900"
          />
        </div>
      </div>

      {/* Align with icon buttons */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Align
        </label>
        <div className="flex gap-1">
          <button
            onClick={() => onUpdate(block.id, { textAlign: 'left' })}
            className={`flex-1 flex items-center justify-center py-2 rounded-lg transition-colors ${
              block.textAlign === 'left'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <AlignLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => onUpdate(block.id, { textAlign: 'center' })}
            className={`flex-1 flex items-center justify-center py-2 rounded-lg transition-colors ${
              block.textAlign === 'center'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <AlignCenter className="w-4 h-4" />
          </button>
          <button
            onClick={() => onUpdate(block.id, { textAlign: 'right' })}
            className={`flex-1 flex items-center justify-center py-2 rounded-lg transition-colors ${
              block.textAlign === 'right'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <AlignRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Line Height - segmented buttons */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Line Height
        </label>
        <div className="flex gap-1">
          {lineHeightOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => onUpdate(block.id, { lineHeight: opt.value })}
              className={`flex-1 py-1.5 text-xs rounded-lg transition-colors ${
                block.lineHeight === opt.value
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Letter Spacing with +/- controls */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Letter Spacing
        </label>
        <div className="flex items-center gap-1">
          <button
            onClick={() => adjustLetterSpacing(-1)}
            className="w-8 h-8 flex items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-100"
          >
            <Minus className="w-3 h-3" />
          </button>
          <input
            type="number"
            value={block.letterSpacing || 0}
            onChange={(e) => onUpdate(block.id, { letterSpacing: parseInt(e.target.value) || 0 })}
            className="flex-1 text-center px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-white text-gray-900"
            min={0}
            max={10}
          />
          <button
            onClick={() => adjustLetterSpacing(1)}
            className="w-8 h-8 flex items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-100"
          >
            <Plus className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Background Color */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Background Color
        </label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={block.backgroundColor || '#ffffff'}
            onChange={(e) => onUpdate(block.id, { backgroundColor: e.target.value })}
            className="w-10 h-10 rounded cursor-pointer border border-gray-300"
          />
          <input
            type="text"
            value={block.backgroundColor || ''}
            onChange={(e) => onUpdate(block.id, { backgroundColor: e.target.value || undefined })}
            className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-white text-gray-900"
            placeholder="transparent"
          />
        </div>
      </div>
    </div>
  );
};

const ImageBlockProperties: React.FC<{
  block: ImageBlock;
  onUpdate: (blockId: string, updates: Partial<ImageBlock>) => void;
}> = ({ block, onUpdate }) => {
  const adjustWidth = (delta: number) => {
    const max = block.widthUnit === '%' ? 100 : 1200;
    const min = block.widthUnit === '%' ? 10 : 50;
    const newValue = Math.min(max, Math.max(min, block.widthValue + delta));
    onUpdate(block.id, { widthValue: newValue });
  };

  const adjustHeight = (delta: number) => {
    if (block.heightValue === null) {
      onUpdate(block.id, { heightValue: 200 });
      return;
    }
    const max = block.heightUnit === '%' ? 100 : 1200;
    const min = block.heightUnit === '%' ? 10 : 50;
    const newValue = Math.min(max, Math.max(min, block.heightValue + delta));
    onUpdate(block.id, { heightValue: newValue });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Image URL</label>
        <input
          type="text"
          value={block.src}
          onChange={(e) => onUpdate(block.id, { src: e.target.value })}
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
          placeholder="https://..."
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Alt Text</label>
        <input
          type="text"
          value={block.alt}
          onChange={(e) => onUpdate(block.id, { alt: e.target.value })}
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
        />
      </div>

      {/* Width */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Width</label>
        <div className="flex items-center gap-1">
          <button
            onClick={() => adjustWidth(-10)}
            className="w-8 h-8 flex items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-100"
          >
            <Minus className="w-3 h-3" />
          </button>
          <input
            type="number"
            value={block.widthValue}
            onChange={(e) => onUpdate(block.id, { widthValue: parseInt(e.target.value) || 100 })}
            className="flex-1 text-center px-2 py-1.5 text-sm border border-gray-300 rounded"
            min={block.widthUnit === '%' ? 10 : 50}
            max={block.widthUnit === '%' ? 100 : 1200}
          />
          <button
            onClick={() => adjustWidth(10)}
            className="w-8 h-8 flex items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-100"
          >
            <Plus className="w-3 h-3" />
          </button>
          <div className="flex border border-gray-300 rounded overflow-hidden ml-1">
            <button
              onClick={() => onUpdate(block.id, { widthUnit: '%', widthValue: Math.min(100, block.widthValue) })}
              className={`px-2 py-1 text-xs ${block.widthUnit === '%' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}
            >
              %
            </button>
            <button
              onClick={() => onUpdate(block.id, { widthUnit: 'px', widthValue: block.widthUnit === '%' ? 400 : block.widthValue })}
              className={`px-2 py-1 text-xs ${block.widthUnit === 'px' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}
            >
              px
            </button>
          </div>
        </div>
      </div>

      {/* Height */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Height</label>
        <div className="flex items-center gap-1">
          <button
            onClick={() => {
              if (block.heightValue === null) {
                onUpdate(block.id, { heightValue: 200 });
              } else {
                adjustHeight(-10);
              }
            }}
            className="w-8 h-8 flex items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-100"
          >
            <Minus className="w-3 h-3" />
          </button>
          <input
            type="text"
            value={block.heightValue === null ? 'auto' : block.heightValue}
            onChange={(e) => {
              const val = e.target.value;
              if (val === 'auto' || val === '') {
                onUpdate(block.id, { heightValue: null });
              } else {
                onUpdate(block.id, { heightValue: parseInt(val) || null });
              }
            }}
            className="flex-1 text-center px-2 py-1.5 text-sm border border-gray-300 rounded"
            placeholder="auto"
          />
          <button
            onClick={() => adjustHeight(10)}
            className="w-8 h-8 flex items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-100"
          >
            <Plus className="w-3 h-3" />
          </button>
          <div className="flex border border-gray-300 rounded overflow-hidden ml-1">
            <button
              onClick={() => onUpdate(block.id, { heightUnit: '%' })}
              className={`px-2 py-1 text-xs ${block.heightUnit === '%' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}
            >
              %
            </button>
            <button
              onClick={() => onUpdate(block.id, { heightUnit: 'px' })}
              className={`px-2 py-1 text-xs ${block.heightUnit === 'px' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}
            >
              px
            </button>
          </div>
        </div>
        <button
          onClick={() => onUpdate(block.id, { heightValue: null })}
          className={`mt-1 text-xs ${block.heightValue === null ? 'text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
        >
          Set to auto
        </button>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Alignment</label>
        <div className="flex gap-1">
          {(['left', 'center', 'right'] as const).map((align) => (
            <button
              key={align}
              onClick={() => onUpdate(block.id, { align })}
              className={`flex-1 py-1.5 text-xs capitalize rounded ${
                block.align === align
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {align}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Link (optional)</label>
        <input
          type="text"
          value={block.link || ''}
          onChange={(e) => onUpdate(block.id, { link: e.target.value || undefined })}
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
          placeholder="https://..."
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Background Color</label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={block.backgroundColor || '#ffffff'}
            onChange={(e) => onUpdate(block.id, { backgroundColor: e.target.value })}
            className="w-10 h-10 rounded cursor-pointer border border-gray-300"
          />
          <input
            type="text"
            value={block.backgroundColor || ''}
            onChange={(e) => onUpdate(block.id, { backgroundColor: e.target.value || undefined })}
            className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
            placeholder="transparent"
          />
        </div>
      </div>
    </div>
  );
};

const ButtonBlockProperties: React.FC<{
  block: ButtonBlock;
  onUpdate: (blockId: string, updates: Partial<ButtonBlock>) => void;
}> = ({ block, onUpdate }) => (
  <div className="space-y-4">
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Button Text</label>
      <input
        type="text"
        value={block.text}
        onChange={(e) => onUpdate(block.id, { text: e.target.value })}
        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
      />
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Link</label>
      <input
        type="text"
        value={block.link}
        onChange={(e) => onUpdate(block.id, { link: e.target.value })}
        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
        placeholder="https://..."
      />
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Background Color</label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={block.backgroundColor}
          onChange={(e) => onUpdate(block.id, { backgroundColor: e.target.value })}
          className="w-10 h-10 rounded cursor-pointer border border-gray-300"
        />
        <input
          type="text"
          value={block.backgroundColor}
          onChange={(e) => onUpdate(block.id, { backgroundColor: e.target.value })}
          className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
        />
      </div>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Text Color</label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={block.textColor}
          onChange={(e) => onUpdate(block.id, { textColor: e.target.value })}
          className="w-10 h-10 rounded cursor-pointer border border-gray-300"
        />
        <input
          type="text"
          value={block.textColor}
          onChange={(e) => onUpdate(block.id, { textColor: e.target.value })}
          className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
        />
      </div>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Border Radius</label>
      <input
        type="range"
        value={block.borderRadius}
        onChange={(e) => onUpdate(block.id, { borderRadius: parseInt(e.target.value) })}
        className="w-full"
        min={0}
        max={32}
      />
      <div className="text-xs text-gray-500 text-center">{block.borderRadius}px</div>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Alignment</label>
      <div className="flex gap-1">
        {(['left', 'center', 'right'] as const).map((align) => (
          <button
            key={align}
            onClick={() => onUpdate(block.id, { align })}
            className={`flex-1 py-1.5 text-xs capitalize rounded ${
              block.align === align
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {align}
          </button>
        ))}
      </div>
    </div>
  </div>
);

const DividerBlockProperties: React.FC<{
  block: DividerBlock;
  onUpdate: (blockId: string, updates: Partial<DividerBlock>) => void;
}> = ({ block, onUpdate }) => (
  <div className="space-y-4">
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Style</label>
      <div className="flex gap-1">
        {(['solid', 'dashed', 'dotted'] as const).map((style) => (
          <button
            key={style}
            onClick={() => onUpdate(block.id, { style })}
            className={`flex-1 py-1.5 text-xs capitalize rounded ${
              block.style === style
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {style}
          </button>
        ))}
      </div>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={block.color}
          onChange={(e) => onUpdate(block.id, { color: e.target.value })}
          className="w-10 h-10 rounded cursor-pointer border border-gray-300"
        />
        <input
          type="text"
          value={block.color}
          onChange={(e) => onUpdate(block.id, { color: e.target.value })}
          className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
        />
      </div>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Thickness</label>
      <input
        type="range"
        value={block.thickness}
        onChange={(e) => onUpdate(block.id, { thickness: parseInt(e.target.value) })}
        className="w-full"
        min={1}
        max={8}
      />
      <div className="text-xs text-gray-500 text-center">{block.thickness}px</div>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Width</label>
      <input
        type="range"
        value={block.width}
        onChange={(e) => onUpdate(block.id, { width: parseInt(e.target.value) })}
        className="w-full"
        min={10}
        max={100}
      />
      <div className="text-xs text-gray-500 text-center">{block.width}%</div>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Background Color</label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={block.backgroundColor || '#ffffff'}
          onChange={(e) => onUpdate(block.id, { backgroundColor: e.target.value })}
          className="w-10 h-10 rounded cursor-pointer border border-gray-300"
        />
        <input
          type="text"
          value={block.backgroundColor || ''}
          onChange={(e) => onUpdate(block.id, { backgroundColor: e.target.value || undefined })}
          className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
          placeholder="transparent"
        />
      </div>
    </div>
  </div>
);

const SpacerBlockProperties: React.FC<{
  block: SpacerBlock;
  onUpdate: (blockId: string, updates: Partial<SpacerBlock>) => void;
}> = ({ block, onUpdate }) => (
  <div className="space-y-4">
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Height</label>
      <input
        type="range"
        value={block.height}
        onChange={(e) => onUpdate(block.id, { height: parseInt(e.target.value) })}
        className="w-full"
        min={8}
        max={120}
      />
      <div className="text-xs text-gray-500 text-center">{block.height}px</div>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Background Color</label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={block.backgroundColor || '#ffffff'}
          onChange={(e) => onUpdate(block.id, { backgroundColor: e.target.value })}
          className="w-10 h-10 rounded cursor-pointer border border-gray-300"
        />
        <input
          type="text"
          value={block.backgroundColor || ''}
          onChange={(e) => onUpdate(block.id, { backgroundColor: e.target.value || undefined })}
          className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
          placeholder="transparent"
        />
      </div>
    </div>
  </div>
);

const FooterBlockProperties: React.FC<{
  block: FooterBlock;
  onUpdate: (blockId: string, updates: Partial<FooterBlock>) => void;
}> = ({ block, onUpdate }) => {
  const [isUploading, setIsUploading] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const updateSocialLink = (key: keyof SocialLinks, value: string) => {
    onUpdate(block.id, {
      socialLinks: { ...block.socialLinks, [key]: value || undefined },
    });
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const publicUrl = await uploadEmailImage(file);
      onUpdate(block.id, { logoUrl: publicUrl });
    } catch (error) {
      console.error('Failed to upload logo:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveLogo = () => {
    onUpdate(block.id, { logoUrl: undefined });
  };

  return (
    <div className="space-y-4">
      {/* Logo Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Logo
        </label>
        <input
          ref={logoInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          onChange={handleLogoUpload}
          className="hidden"
        />
        
        {block.logoUrl ? (
          <div className="space-y-3">
            {/* Preview */}
            <div className="relative rounded-lg overflow-hidden border border-gray-200 bg-gray-50 p-2">
              <img 
                src={block.logoUrl} 
                alt="Logo" 
                className="max-h-16 mx-auto object-contain"
                style={{ maxWidth: block.logoWidth }}
              />
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center gap-2 opacity-0 hover:opacity-100 transition-opacity">
                <button
                  onClick={() => logoInputRef.current?.click()}
                  className="px-2 py-1 text-xs bg-white text-gray-700 rounded hover:bg-gray-100"
                >
                  Replace
                </button>
                <button
                  onClick={handleRemoveLogo}
                  className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          </div>
        ) : (
          <button
            onClick={() => logoInputRef.current?.click()}
            disabled={isUploading}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50/50 transition-colors disabled:opacity-50"
          >
            {isUploading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Upload Logo
              </>
            )}
          </button>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Logo Width
        </label>
        <input
          type="number"
          value={block.logoWidth}
          onChange={(e) => onUpdate(block.id, { logoWidth: parseInt(e.target.value) || 150 })}
          className="w-full px-2 py-1.5 text-sm border border-gray-300  rounded-lg"
          min={50}
          max={300}
        />
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="logoAsAvatar"
          checked={block.logoAsAvatar || false}
          onChange={(e) => onUpdate(block.id, { logoAsAvatar: e.target.checked })}
          className="rounded border-gray-300"
        />
        <label htmlFor="logoAsAvatar" className="text-sm text-gray-700">
          Display as circular avatar
        </label>
      </div>

      {/* Social Links */}
      <div>
        <label className="block text-sm font-medium text-gray-700  mb-2">
          Social Links
        </label>
        <div className="space-y-2">
          <input
            type="text"
            value={block.socialLinks.facebook || ''}
            onChange={(e) => updateSocialLink('facebook', e.target.value)}
            className="w-full px-2 py-1.5 text-sm border border-gray-300  rounded-lg"
            placeholder="Facebook URL"
          />
          <input
            type="text"
            value={block.socialLinks.instagram || ''}
            onChange={(e) => updateSocialLink('instagram', e.target.value)}
            className="w-full px-2 py-1.5 text-sm border border-gray-300  rounded-lg"
            placeholder="Instagram URL"
          />
          <input
            type="text"
            value={block.socialLinks.twitter || ''}
            onChange={(e) => updateSocialLink('twitter', e.target.value)}
            className="w-full px-2 py-1.5 text-sm border border-gray-300  rounded-lg"
            placeholder="X (Twitter) URL"
          />
          <input
            type="text"
            value={block.socialLinks.linkedin || ''}
            onChange={(e) => updateSocialLink('linkedin', e.target.value)}
            className="w-full px-2 py-1.5 text-sm border border-gray-300  rounded-lg"
            placeholder="LinkedIn URL"
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="showSocialIcons"
          checked={block.showSocialIcons}
          onChange={(e) => onUpdate(block.id, { showSocialIcons: e.target.checked })}
          className="rounded border-gray-300"
        />
        <label htmlFor="showSocialIcons" className="text-sm text-gray-700 ">
          Show social icons
        </label>
      </div>

      {/* Contact Info */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Contact Email
        </label>
        <input
          type="text"
          value={block.contactEmail || ''}
          onChange={(e) => onUpdate(block.id, { contactEmail: e.target.value })}
          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg font-mono"
          placeholder="{{practice_email}}"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Contact Phone
        </label>
        <input
          type="text"
          value={block.contactPhone || ''}
          onChange={(e) => onUpdate(block.id, { contactPhone: e.target.value })}
          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg font-mono"
          placeholder="{{practice_phone}}"
        />
      </div>

      {/* Address */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Address
        </label>
        <textarea
          value={block.addressText}
          onChange={(e) => onUpdate(block.id, { addressText: e.target.value })}
          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg font-mono"
          rows={2}
          placeholder="{{practice_address}}"
        />
      </div>

      {/* Unsubscribe */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Unsubscribe Text
        </label>
        <input
          type="text"
          value={block.unsubscribeText}
          onChange={(e) => onUpdate(block.id, { unsubscribeText: e.target.value })}
          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg"
          placeholder="Unsubscribe"
        />
        <p className="mt-1 text-xs text-gray-500">
          The unsubscribe link is automatically generated for each recipient.
        </p>
      </div>

      {/* Legal Text */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Legal Text
        </label>
        <textarea
          value={block.legalText || ''}
          onChange={(e) => onUpdate(block.id, { legalText: e.target.value })}
          className="w-full px-2 py-1.5 text-sm border border-gray-300  rounded-lg"
          rows={3}
        />
      </div>

      {/* Colors */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Background Color
        </label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={block.backgroundColor}
            onChange={(e) => onUpdate(block.id, { backgroundColor: e.target.value })}
            className="w-10 h-10 rounded cursor-pointer border border-gray-300"
          />
          <input
            type="text"
            value={block.backgroundColor}
            onChange={(e) => onUpdate(block.id, { backgroundColor: e.target.value })}
            className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-lg"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Text Color
        </label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={block.textColor}
            onChange={(e) => onUpdate(block.id, { textColor: e.target.value })}
            className="w-10 h-10 rounded cursor-pointer border border-gray-300"
          />
          <input
            type="text"
            value={block.textColor}
            onChange={(e) => onUpdate(block.id, { textColor: e.target.value })}
            className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-lg"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Link Color
        </label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={block.linkColor}
            onChange={(e) => onUpdate(block.id, { linkColor: e.target.value })}
            className="w-10 h-10 rounded cursor-pointer border border-gray-300"
          />
          <input
            type="text"
            value={block.linkColor}
            onChange={(e) => onUpdate(block.id, { linkColor: e.target.value })}
            className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-lg"
          />
        </div>
      </div>
    </div>
  );
};

